<template>
  <div>
    <b-row>
      <div class="mb-3 col-md">
        <treeselect
          ref="structureTreeselect"
          instance-id="structure"
          v-model="structureValue"
          @update:model-value="filterInput"
          @close="onTreeselectClose"
          @open="onTreeselectOpen"
          :key="treeKey"
          :auto-load-root-options="false"
          :placeholder="$t('Webportal.StructureFilter.Placeholder')"
          :options="structureItems"
          :disabled="disabledInternal || !structureItems || structureItems.length === 0"
          :disableFuzzyMatching="true"
          :multiple="multipleSurveySelectionInternal"
          :disable-branch-nodes="!multipleSurveySelectionInternal"
          :showCount="true"
          showCountOf="LEAF_DESCENDANTS"
          valueFormat="object">
        </treeselect>
      </div>
      <div class="mb-3 col-md" v-if="isDateRangeFilterEnabled">
        <date-range-picker
          ref="dateRangePicker"
          v-model="dateRange"
          :locale-data="localeData"
          :ranges="false"
          :time-picker="false"
          :auto-apply="true"
          :show-week-numbers="true"
          :show-dropdowns="true"
          :disabled="disabledInternal"
          opens="left"
          @toggle="toggleDateRange"
          @update="loadData"
          >
          <template #input="picker">
            <font-awesome-icon :icon="['fas', 'calendar']" class="mr-1" />
            <span v-show="picker.startDate">
              {{ msm_date(picker.startDate) }} - {{ msm_date(picker.endDate) }}
            </span>
            <span class="placeholder" v-show="picker.startDate == null">
              {{ $t('Webportal.DateRangeFilter.Placeholder') }}
            </span>
            <b-button class="btn-close" v-show="picker.startDate" @click.stop="clearDateRange()"></b-button>
          </template>
          <template #date="data">
            <div>{{ data.date.getDate() }}</div>
            <div class="check-count">
              <span>&nbsp;</span>
              <span v-if="getCheckCount(data.date)" class="badge badge-secondary">n={{ getCheckCount(data.date) }}</span>
              <span v-else>&nbsp;</span>
            </div>
          </template>
          <template #footer="">
            <div v-show="isLoadingCheckCounts" class="text-center mb-2">
              {{ $t('Webportal.DateRangeFilter.LoadingCheckCounts') }} <b-spinner small variant="secondary"></b-spinner>
            </div>
          </template>
        </date-range-picker>
      </div>
    </b-row>
    <b-row>
      <div class="mb-3 col-md">
        <treeselect
          ref="waveTreeselect"
          instance-id="wave"
          v-model="waveValue"
          @update:model-value="filterInput"
          @close="onTreeselectClose"
          @open="onTreeselectOpen"
          :key="treeKey"
          :auto-load-root-options="false"
          :placeholder="$t('Webportal.WaveFilter.Placeholder')"
          :options="waveItems"
          :disabled="disabledInternal || !waveItems || waveSelectDisabled"
          :disableFuzzyMatching="true"
          :multiple="true"
          :showCount="true"
          showCountOf="LEAF_DESCENDANTS"
          :flat="true"
          :defaultExpandLevel="1"
          :disable-branch-nodes="true"
          :limit="4"
          :limit-text="limitText">
        </treeselect>
      </div>
      <div class="mb-3 col-md">
        <treeselect
          ref="scenarioTreeselect"
          instance-id="scenario"
          v-model="scenarioValue"
          @update:model-value="filterInput"
          @close="onTreeselectClose"
          @open="onTreeselectOpen"
          :key="treeKey"
          :auto-load-root-options="false"
          :placeholder="$t('Webportal.ScenarioFilter.Placeholder')"
          :options="scenarioItems"
          :disabled="disabledInternal || scenarioSelectDisabled"
          :disableFuzzyMatching="true"
          :multiple="true"
          :showCount="true"
          showCountOf="LEAF_DESCENDANTS"
          :flat="true"
          :defaultExpandLevel="1"
          :disable-branch-nodes="true"
          :limit="4"
          :limit-text="limitText">
        </treeselect>
      </div>
      <div class="mb-3 col-md">
        <treeselect
          ref="propertyTreeselect"
          instance-id="property"
          v-model="propertyValue"
          @update:model-value="filterInput"
          @close="onTreeselectClose"
          @open="onTreeselectOpen"
          :key="treeKey"
          :auto-load-root-options="false"
          :placeholder="$t('Webportal.PropertyValueFilter.Placeholder')"
          :options="propertyItems"
          :disableFuzzyMatching="true"
          :disabled="disabledInternal || propertySelectDisabled"
          :multiple="true"
          :showCount="true"
          showCountOf="LEAF_DESCENDANTS"
          :defaultExpandLevel="1"
          :disable-branch-nodes="false"
          value-consists-of="LEAF_PRIORITY"
          :limit="4"
          :limit-text="limitText">
        </treeselect>
      </div>
    </b-row>
  </div>
</template>

<script>
import AppConfig from '@/mixins/AppConfig-APP_TARGET';
import moment from "moment";
import DateRangePicker from 'vue3-daterange-picker';

import EventBus from '@/services/EventBus.js';
import AuthenticationService from '@/services/AuthenticationService';
import AdministrationService from '@/services/AdministrationService'
import SurveysService from '@/services/SurveysService';

import CustomFilter from '@/plugins/CustomFilter';

export default {
  name: "SurveyFilter",

  mixins: [ AppConfig ],

  components: { DateRangePicker },

  props: {
    value: null,
    disabled: { default: false },
    multipleSurveySelection: { default: true },
    showCheckCount: { default: true },
    showDateRangeFilter: { default: true }
  },

  data() {
    return {
      treeKey: 0,
      treeselectOldValue: {},

      disabledInternal: this.disabled,
      multipleSurveySelectionInternal: this.multipleSurveySelection,

      clientSettings: null,
      structureItems: [],                   // Data items for structure treeselect
      structureValue: this.multipleSurveySelectionInternal ? [] : null, // Selected structure items
      dateRange: {},
      checkCountMap: null,
      maxCheckDate: null,
      waveItems: [],                        // Data items for wave treeselect
      waveValue: [],                        // Selected wave items
      scenarioItems: [],                    // Data items for scenarios
      scenarioValue: [],                    // Selected scenarios
      propertyItems: [],                    // Data items for property treeselect
      propertyValue: [],                    // Selected property items

      currentStructureValue: null,          // Current selected structure items for change detection
      currentFilter: null,                  // Current filter object for change detection

      isLoadingCheckCounts: false
    }
  },

  computed: {
    isDateRangeFilterEnabled() {
      return this.clientSettings && this.clientSettings.DateRangeFilterMode > 0 && this.showDateRangeFilter;
    },

    localeData() {
      return {
        format: moment.localeData().longDateFormat('L').toLowerCase(),
        weekLabel: this.$t('Webportal.CalendarWeekShort'),
        daysOfWeek: moment.localeData().weekdaysShort(),
        monthNames: moment.localeData().months(),
        firstDay: 1
      };
    },

    waveSelectDisabled() {
      return _.isEmpty(this.structureValue);
    },

    scenarioSelectDisabled() {
      return _.isEmpty(this.structureValue);
    },

    propertySelectDisabled() {
      return _.isEmpty(this.structureValue);
    },

    filter() {
      return {
        clientIds: Array.isArray(this.structureValue) ? this.structureValue.filter(item => item.id[0] === 'C').map(item => item.id.substring(1)) : [],
        projectIds: Array.isArray(this.structureValue) ? this.structureValue.filter(item => item.id[0] === 'P').map(item => item.id.substring(1)) : [],
        surveyIds: Array.isArray(this.structureValue) ? this.structureValue.filter(item => item.id[0] === 'S').map(item => item.id.substring(1)) :
          this.structureValue ? [ this.structureValue.id.substring(1) ] : [],
        // Includes survey ids from selected clients and projects
        computedSurveyIds: Array.isArray(this.structureValue) ?
          _.flatMapDeep(
            _.flatMapDeep(
              this.structureValue,
              item => item.id[0] !== 'S' ? item.children : item),
            item => item.id[0] !== 'S' ? item.children : item)
          .filter(item => item && item.id[0] === 'S')
          .map(item => item.id.substring(1)) :
          this.structureValue ? [ this.structureValue.id.substring(1) ] : [],
        waveIds: this.waveValue,
        scenarioIds: this.scenarioValue,
        dateFrom: this.isDateRangeFilterEnabled ? this.dateRange.startDate : null,
        dateTo: this.isDateRangeFilterEnabled ? this.dateRange.endDate : null,
        propertyIds: this.propertyValue,
        propertyUseLogicalAnd: false
      };
    },

    options() {
      return {
        caller: this.getCaller(this.$route.path)
      }
    }
  },

  watch: {
    currentFilter(newVal, oldVal) {
      this.$emit('update:modelValue', newVal);
    },

    disabled(newVal, oldVal) {
      this.disabledInternal = newVal;
    },

    disabledInternal(newVal, oldVal) {
      this.$emit("disabledChanged", newVal);
    },

    isDateRangeFilterEnabled(newVal, oldVal) {
      this.currentFilter = this.filter;
    }
  },

  mounted() {
    // Preselect configured surveys for client users
    if (!AuthenticationService.isMSMUser && AuthenticationService.localUserProfile.ClientId) {
      Promise.all([
        this.getClientSettings(),
        this.getSurveyStructure()
      ])
        .then(() => {
          const availableSurveys = _.flatMapDeep(
            _.flatMapDeep(
              this.structureItems,
              item => item.id[0] !== 'S' ? item.children : item
            ),
            item => item.id[0] !== 'S' ? item.children : item)
            .filter(item => item && item.id[0] === 'S');

          const surveyPreselection = availableSurveys.filter(s => this.clientSettings?.SurveyPreselection?.some(surveyId => s.Survey.SurveyId == surveyId));
          if (!_.isEmpty(surveyPreselection)) {
            this.structureValue = this.multipleSurveySelectionInternal ? surveyPreselection : surveyPreselection[0];
            this.currentStructureValue = this.structureValue;
          }

          this.currentFilter = this.filter;
          this.treeKey++;
        });
    }
    else
    {
      this.currentFilter = this.filter;
      this.getSurveyStructure();
    }

    this.getSurveyWaves();
    this.getSurveyProperties();
    this.getSurveyScenarios();

    EventBus.$on('LocalizationChanged', this.localizationChanged);
  },

  beforeUnmount() {
    EventBus.$off('LocalizationChanged', this.localizationChanged);
  },

  methods: {
    msm_date(val) {
      return CustomFilter.msm_date(val)
    },

    localizationChanged(localizationId) {
      this.getSurveyStructure();
      this.getSurveyWaves();
      this.getSurveyScenarios();
    },

    getClientSettings() {
      return AdministrationService.getClientSettings(AuthenticationService.localUserProfile.ClientId)
        .then(response => {
          this.clientSettings = response.data[0];
        });
    },

    // Get survey structure data for the structure treeselect
    getSurveyStructure() {
      return SurveysService.getSurveyStructure()
        .then(response => {
          this.structureItems = response.data;
        })
    },

    // Get survey structure data for the structure treeselect
    getSurveyWaves() {
      if (!this.waveSelectDisabled)
      {
        this.waveItems = [];
        SurveysService.getSurveyWaves(this.filter)
          .then(response => {
            this.waveItems = response.data;
          });
      }
    },

    getSurveyScenarios() {
      if (!this.scenarioSelectDisabled)
      {
        this.scenarioItems = [];
        SurveysService.getSurveyScenarios(this.filter)
          .then(response => {
            this.scenarioItems = response.data;
          })
      }
    },

    // Get survey structure data for the structure treeselect
    getSurveyProperties() {
      if (!this.propertySelectDisabled)
      {
        this.propertyItems = [];
        SurveysService.getSurveyCustomProperties(this.filter, true)
          .then(response => {
            this.propertyItems = response.data;
          })
      }
    },

    onTreeselectOpen(value, instanceId) {
      const ids = Array.isArray(value) ? value.map(v => v.id) : value?.id
      this.treeselectOldValue[instanceId] = JSON.stringify(ids);
    },

    onTreeselectClose(value, instanceId) {
      const ids = Array.isArray(value) ? value.map(v => v.id) : value?.id
      if (this.treeselectOldValue[instanceId] === JSON.stringify(ids))
        return;

      this.loadData();
    },

    // Called whenever the input of a treeselect control changes
    filterInput(value, instanceId) {
      if (!this.$refs[`${instanceId}Treeselect`] || this.$refs[`${instanceId}Treeselect`]?.menu?.isOpen)
        return;

      // Workaround: Only call data loading when clear all is pressed
      if (_.size(value) === 0 || (!this.multipleSurveySelection && instanceId == "structure")) {
        this.loadData();
      }
    },

    // Applies changed filter and triggeres data loading if necessary
    loadData() {
      // Only load data when effective filter has changed
      if (!_.isEqual(this.filter, this.currentFilter)) {
        let structureValueChanged = !_.isEqual(this.currentStructureValue, this.structureValue);
        this.currentStructureValue = this.structureValue;
        if (structureValueChanged)
        {
          this.waveValue = [];
          this.propertyValue = [];
          this.scenarioValue = [];
        }

        this.checkCountMap = null;
        this.maxCheckDate = null;
        this.currentFilter = this.filter;

        if (structureValueChanged)
        {
          this.getSurveyWaves();
          this.getSurveyProperties();
          this.getSurveyScenarios();
        }
      }
    },

    async toggleDateRange(show, event) {
      if (!show)
        return;

      if (this.checkCountMap == null && this.showCheckCount)
      {
        try {
          this.isLoadingCheckCounts = true;

          const checkCountResult = await SurveysService.getSurveyInstanceCount(this.filter, this.options);

          if (checkCountResult?.data) {
            this.checkCountMap = new Map();

            for (const entry of checkCountResult.data)
            {
              if (entry.date == null) continue;

              const date = moment(entry.date);
              if (this.maxCheckDate < date)
              this.maxCheckDate = date;
              this.checkCountMap.set(date.format('YYYY-MM-DD'), entry.checkCount);
            }
          }
        }
        finally {
          this.isLoadingCheckCounts = false;
        }
      }

      if (this.dateRange.startDate == null && this.maxCheckDate)
      {
        // ensure to show latest check date on the right date range picker
        this.$nextTick(() => {
          this.$refs.dateRangePicker.changeRightMonth({ year: this.maxCheckDate.year(), month: this.maxCheckDate.month() + 1 })
        });
      }
    },

    getCheckCount(date) {
      return this.checkCountMap?.get(moment(date).format('YYYY-MM-DD'));
    },

    clearDateRange() {
      this.dateRange.startDate = null;
      this.dateRange.endDate = null;

      this.loadData();
    },
  }
}
</script>
